import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../../requestOptions';
import type { ExpansionHistoryRow, ExpansionRunStatus } from './types';

/** The server's own cap on `per_page` (`ExpansionController::MAX_HISTORY_PER_PAGE`). */
export const RUN_HISTORY_MAX_PER_PAGE = 100;

/** `source` on `GET expansion/history`: every run (the API's own default), unfiltered runs only, or one named subscription. */
export type ExpansionHistorySourceFilter = 'all' | 'none' | number;

const validate = (subscriptionId: number, page?: number, perPage?: number, destinationSubscriptionId?: number) => {
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
  if (page !== undefined && (!page || page < 1)) {
    throw new Error('page must be a positive number');
  }
  if (perPage !== undefined && (!perPage || perPage < 1 || perPage > RUN_HISTORY_MAX_PER_PAGE)) {
    throw new Error(`perPage must be between 1 and ${RUN_HISTORY_MAX_PER_PAGE}`);
  }
  if (destinationSubscriptionId !== undefined && (!destinationSubscriptionId || destinationSubscriptionId < 1)) {
    throw new Error('Destination subscription ID must be a positive number');
  }
};

type RequestParams = {
  /**
   * Any subscription of the organization — the API authorises the caller against it and scopes
   * every read by organization, never by this id.
   */
  subscriptionId: number;
  page?: number;
  perPage?: number;
  destinationSubscriptionId?: number;
  status?: ExpansionRunStatus;
  /** Omitted is the API's own default, `'all'`. */
  source?: ExpansionHistorySourceFilter;
};

export type ListRunHistoryResponse = {
  data: ExpansionHistoryRow[];
  total: number;
};

export const listRunHistory = async (
  scope: RequestScope,
  { subscriptionId, page, perPage, destinationSubscriptionId, status, source }: RequestParams,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<ListRunHistoryResponse>> => {
  try {
    validate(subscriptionId, page, perPage, destinationSubscriptionId);

    const result = await scopedChannelApi(scope).get<ListRunHistoryResponse>('atts/expansion/history', {
      ...options,
      headers: { 'x-astur-subscription-id': subscriptionId.toString() },
      params: {
        page,
        per_page: perPage,
        destination_subscription_id: destinationSubscriptionId,
        status,
        source,
      },
    });

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
